import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { Paper, Box } from '@mui/material';
import {
  formatRupeesIndianCompact,
  premiumTooltip,
  standardGrid,
  horizontalGrid,
  indianValueAxis,
  indianCategoryAxis,
  STATUS_COLORS,
  defaultAnimation,
} from './echartsConfig';
import { reportChartCanvasSx, reportChartPaperSx } from './reportChartLayout';
import ReportCardHeader, { ReportInsightBanner } from './ReportCardHeader';
import ReportKpiStrip, { kpiMapToItems } from './ReportKpiStrip';

const HORIZONTAL_THRESHOLD = 6;

const toNum = v => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const norm = s => String(s || '').toLowerCase().trim();

function datasetIndices(datasets) {
  const idx = needle => datasets.findIndex(d => norm(d.label).includes(needle));
  return { iConf: idx('confirm'), iTent: idx('tent'), iWait: idx('wait') };
}

function rowTotal(datasets, idxMap, i) {
  const { iConf, iTent, iWait } = idxMap;
  let t = 0;
  if (iConf >= 0) t += toNum(datasets[iConf].data[i]);
  if (iTent >= 0) t += toNum(datasets[iTent].data[i]);
  if (iWait >= 0) t += toNum(datasets[iWait].data[i]);
  return t;
}

function prepareVenueBobDisplay(data) {
  const rawLabels = (data?.labels || []).map(l => String(l ?? ''));
  const rawDatasets = (data?.datasets || []).map(ds => ({
    ...ds,
    data: (ds.data || []).map(toNum),
  }));

  const idxMap = datasetIndices(rawDatasets);
  const n = rawLabels.length;

  if (n === 0) {
    return { labels: [], datasets: [], rowTotals: [], grandTotal: 0, venueCount: 0 };
  }

  const rows = [];
  for (let i = 0; i < n; i++) {
    rows.push({
      label: rawLabels[i],
      conf: idxMap.iConf >= 0 ? toNum(rawDatasets[idxMap.iConf].data[i]) : 0,
      tent: idxMap.iTent >= 0 ? toNum(rawDatasets[idxMap.iTent].data[i]) : 0,
      wait: idxMap.iWait >= 0 ? toNum(rawDatasets[idxMap.iWait].data[i]) : 0,
      total: rowTotal(rawDatasets, idxMap, i),
    });
  }

  rows.sort((a, b) => b.total - a.total);
  const grandTotal = rows.reduce((s, r) => s + r.total, 0);
  const venueCount = rows.length;

  const labels = rows.map(r => r.label);
  const datasets = rawDatasets.map((ds, di) => {
    const newData = rows.map(r => {
      if (di === idxMap.iConf) return r.conf;
      if (di === idxMap.iTent) return r.tent;
      if (di === idxMap.iWait) return r.wait;
      return 0;
    });
    return { ...ds, data: newData };
  });

  const rowTotals = rows.map(r => r.total);

  return { labels, datasets, rowTotals, grandTotal, venueCount };
}

function computeVenueKpis(labels, rowTotals, grandTotal, venueCount) {
  if (!labels.length) {
    return { topVenue: '—', largestContribution: '—', venueCountDisplay: '—', avgRevenue: '—' };
  }

  const topRev = rowTotals[0] || 0;
  const pct = grandTotal > 0 && topRev > 0 ? `${Math.round((topRev / grandTotal) * 1000) / 10}%` : '—';

  return {
    topVenue: String(labels[0] ?? '—'),
    largestContribution: pct,
    venueCountDisplay: String(venueCount),
    avgRevenue: formatRupeesIndianCompact(venueCount > 0 ? grandTotal / venueCount : 0),
  };
}

function getSeriesColor(label) {
  const key = norm(label);
  if (key.includes('confirm')) return STATUS_COLORS.confirmed;
  if (key.includes('tent')) return STATUS_COLORS.tentative;
  if (key.includes('wait')) return STATUS_COLORS.waitlisted;
  return '#90a4ae';
}

const VenueWiseBobChart = ({ data, name, handleExport, showButton = true }) => {
  const displayName = data?.name || name;

  const prepared = useMemo(() => prepareVenueBobDisplay(data), [data]);

  const kpis = useMemo(
    () => computeVenueKpis(prepared.labels, prepared.rowTotals, prepared.grandTotal, prepared.venueCount),
    [prepared],
  );

  const isHorizontal = prepared.labels.length > HORIZONTAL_THRESHOLD;

  const option = useMemo(() => {
    const { labels, datasets, rowTotals, grandTotal } = prepared;

    const categoryAxis = {
      ...indianCategoryAxis,
      data: labels,
      ...(isHorizontal && {
        axisLabel: { ...indianCategoryAxis.axisLabel, width: 110, overflow: 'truncate' },
      }),
    };

    const valueAxis = { ...indianValueAxis };

    const series = datasets.map(ds => ({
      name: ds.label || '',
      type: 'bar',
      stack: 'total',
      barWidth: isHorizontal ? '55%' : '50%',
      itemStyle: { color: getSeriesColor(ds.label), borderRadius: 0 },
      emphasis: { itemStyle: { opacity: 0.85 } },
      data: ds.data,
    }));

    return {
      ...(isHorizontal ? horizontalGrid : standardGrid),
      grid: isHorizontal ? horizontalGrid : standardGrid,
      tooltip: {
        ...premiumTooltip,
        trigger: 'axis',
        formatter(params) {
          if (!Array.isArray(params) || params.length === 0) return '';
          const idx = params[0].dataIndex;
          const total = rowTotals[idx] || 0;
          const pct = grandTotal > 0 ? Math.round((total / grandTotal) * 1000) / 10 : 0;
          const header = `<div style="font-weight:600;font-size:13px;color:#f8fafc;margin-bottom:6px">${labels[idx]} — ${formatRupeesIndianCompact(total)} (${pct}%)</div>`;
          const rows = params
            .filter(p => p.value != null && p.value !== 0)
            .map(p => {
              const marker = `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${p.color};margin-right:6px"></span>`;
              return `<div style="display:flex;justify-content:space-between;align-items:center;gap:12px">${marker}<span style="flex:1">${p.seriesName}</span><span style="font-weight:600;font-variant-numeric:tabular-nums">${formatRupeesIndianCompact(p.value)}</span></div>`;
            })
            .join('');
          return header + rows;
        },
      },
      xAxis: isHorizontal ? valueAxis : categoryAxis,
      yAxis: isHorizontal ? categoryAxis : valueAxis,
      series,
      ...defaultAnimation,
    };
  }, [prepared, isHorizontal]);

  const plotHeight = useMemo(() => {
    if (isHorizontal) {
      const n = prepared.labels.length || 1;
      return Math.min(440, Math.max(200, n * 30));
    }
    return undefined;
  }, [isHorizontal, prepared.labels.length]);

  const canvasSx = isHorizontal
    ? { ...reportChartCanvasSx, height: plotHeight, minHeight: plotHeight, maxHeight: plotHeight }
    : reportChartCanvasSx;

  const apiKpis = kpiMapToItems(data?.kpiMap);
  const infoTooltip = data?.infoText || `Venue contribution breakdown${isHorizontal ? ' (horizontal — more than 5 venues)' : ''}`;

  return (
    <Paper sx={reportChartPaperSx}>
      <ReportCardHeader
        title={displayName}
        insight={infoTooltip}
        handleExport={handleExport}
        showButton={showButton}
      />

      <ReportKpiStrip
        columns={4}
        items={apiKpis || [
          { label: 'Top Venue', value: kpis.topVenue },
          { label: 'Largest Contribution', value: kpis.largestContribution },
          { label: 'Avg Revenue / Venue', value: kpis.avgRevenue },
        ]}
      />

      <ReportInsightBanner text={data?.insightText} style={data?.insightStyle} />

      <Box sx={canvasSx}>
        <ReactECharts
          option={option}
          style={{ width: '100%', height: '100%' }}
          notMerge
          lazyUpdate
        />
      </Box>
    </Paper>
  );
};

export default VenueWiseBobChart;
